import {
  runInAction,
  makeAutoObservable,
  reaction,
  when,
  onBecomeObserved,
  onBecomeUnobserved,
} from "mobx";
import localforage from "localforage";
import type RootStore from "./RootStore";
import type { FeathersService } from "@feathersjs/feathers";
import { ReactionManager } from "./utils/ReactionManager";

const SORT_DIRECTION_OPTIONS = ["ASC", "DESC"];

interface ServiceStoreOptions {
  apiVersion?: number;
  channels?: boolean;
  debugMode?: boolean;
  enableErrorNotifications?: boolean;
  disableCreatedListener?: boolean;
  disableUpdatedListener?: boolean;
  disablePatchedListener?: boolean;
  disableRemovedListener?: boolean;
  disableStatusListener?: boolean;
  enableEntityOptimization?: boolean;
  pagination?: number;
  enableStorage?: boolean;
  idField?: string;
}

interface ErrorHandlerError {
  name: string;
  message: string;
  hook?: {
    id?: number;
  };
}

interface ServiceStoreItem {
  id: number | string;
  [key: string]: unknown;
}

type GetResponse = ServiceStoreItem;

interface FindResponse {
  data: ServiceStoreItem[];
  result?: { data: ServiceStoreItem[] };
  total: number;
}

type PatchResponse = ServiceStoreItem;
type CreateResponse = ServiceStoreItem;
type UpdateResponse = ServiceStoreItem;
type RemoveResponse = ServiceStoreItem;

interface ServiceError {
  name: string;
  message: string;
  hook?: {
    id?: number;
  };
}

export default class ServiceStore {
  rootStore;
  debugMode: boolean = false;

  isServiceStore = true;

  service: FeathersService;
  storage: LocalForage | null = null;
  serviceRoute = "";
  firstLoad = false;

  name: string | undefined = undefined;
  idField: string = "id";
  jobNumberService: boolean = false;
  options: ServiceStoreOptions = {};
  apiVersion: number = 1;
  channelsEnabled: boolean = true;
  enableErrorNotifications: boolean = false;
  enableEntityOptimization: boolean = false;

  disableCreatedListener: boolean = false;
  disableUpdatedListener: boolean = false;
  disablePatchedListener: boolean = false;
  disableRemovedListener: boolean = false;
  disableStatusListener: boolean = false;

  items: ServiceStoreItem[] = [];
  isBeingWatched = false;
  enableAutoChannel = false;

  errorOnGet: ServiceError | null = null;
  errorOnFind: ServiceError | null = null;
  errorOnCreate: ServiceError | null = null;
  errorOnPatch: ServiceError | null = null;
  errorOnUpdate: ServiceError | null = null;
  errorOnRemove: ServiceError | null = null;

  isGetPending = false;
  isFindPending = false;
  isCreatePending = false;
  isPatchPending = false;
  isUpdatePending = false;
  isRemovePending = false;

  pagination = {
    enabled: false,
    total: 0,
    currentPage: 0,
    pageSize: 250,
    query: {},
    sortKey: "",
    sortDirection: SORT_DIRECTION_OPTIONS[0],
  };

  pageDataSet: { pageNumber: number; pageItems: ServiceStoreItem[] }[] = [];

  firsLoadDispose: (() => void) | undefined;
  disposeEntityOptimization: (() => void) | undefined;
  reactionManager: ReactionManager | null = null;
  errorReactionsBatchDispose: (() => void) | undefined;

  /**
   *
   * @param {object} rootStore
   * @param {string} name
   * @param {object} options
   */
  constructor(
    rootStore: RootStore,
    name: string,
    options: ServiceStoreOptions
  ) {
    this.name = name;
    this.rootStore = rootStore;
    this.apiVersion = options?.apiVersion ? options.apiVersion : 1;
    this.serviceRoute =
      this.apiVersion > 1 ? `v${this.apiVersion}/${name}` : name;
    this.service = rootStore.app.service(this.serviceRoute);
    this.channelsEnabled =
      options.channels || rootStore.mode !== "test" || true;
    this.debugMode = options.debugMode || false;
    this.enableErrorNotifications = options.enableErrorNotifications || false;
    this.disableCreatedListener =
      rootStore.mode === "test" || options.disableCreatedListener || false;
    this.disableUpdatedListener =
      rootStore.mode === "test" || options.disableUpdatedListener || false;
    this.disablePatchedListener =
      rootStore.mode === "test" || options.disablePatchedListener || false;
    this.disableRemovedListener =
      rootStore.mode === "test" || options.disableRemovedListener || false;
    this.disableStatusListener =
      rootStore.mode === "test" || options.disableStatusListener || false;

    this.enableEntityOptimization = options.enableEntityOptimization || false;

    // Only call makeAutoObservable if this is a direct ServiceStore instance,
    // not a subclass. Subclasses must handle their own observability with makeObservable.
    if (this.constructor === ServiceStore) {
      makeAutoObservable(this, {
        rootStore: false,
      });
    }

    if (options.idField) {
      // if service has socket
      this.setServiceIdField(options.idField);
    }
    // console.log(`ServiceStore ${name} channelsEnabled ${this.channelsEnabled}`);
    if (this.channelsEnabled) this.handleEvents();
    if (options.pagination) this.enablePagination(options.pagination);
    if (options.enableStorage) {
      this.storage = localforage.createInstance({
        name: this.rootStore.appName,
        storeName: this.name,
      });

      this.firsLoadDispose = when(
        () => this.shouldRunFirstLoad,
        () => {
          return this.loadStorage();
        }
      );
    }

    if (this.enableEntityOptimization) {
      this.disposeEntityOptimization = reaction(
        () => rootStore.currentChannelServices,
        (channelReaction: string[]) => {
          const serviceInChannels = channelReaction.findIndex(
            (
              /** @type {string | string[]} */ channelRoute: string | string[]
            ) => channelRoute.includes(`${this.name}`)
          );
          const shouldWatch = serviceInChannels > -1;
          if (shouldWatch) {
            this.debugMode && console.warn("shouldWatch", this.name);
            return this.setObservability(true);
          }
          return this.setObservability(false);
        },
        { fireImmediately: true }
      );
    } else {
      this.handleFirstLoad();
    }

    // OPTIMIZED: Replace 5 individual error reactions with batched reaction
    this.reactionManager = new ReactionManager(this.debugMode);
    this.reactionManager.createReactionBatch(`${this.name}-errors`, {
      delay: 50, // Batch error handling with 50ms delay
      maxWait: 200, // Maximum wait before forcing execution
      trailing: true
    });

    // Add all error reactions to the batch
    this.reactionManager.addToBatch(
      `${this.name}-errors`,
      'all-errors',
      () => ({
        create: this.errorOnCreate,
        patch: this.errorOnPatch,
        remove: this.errorOnRemove,
        find: this.errorOnFind,
        get: this.errorOnGet
      }),
      (current, previous) => {
        // Handle any new errors in batch
        Object.entries(current).forEach(([operation, error]) => {
          const prevError = previous?.[operation as keyof typeof previous];
          if (error && error !== prevError) {
            this.errorHandler(error);
          }
        });
      },
      { fireImmediately: true }
    );

    this.reactionManager.activateBatch(`${this.name}-errors`);
    this.errorReactionsBatchDispose = () => {
      this.reactionManager?.disposeBatch(`${this.name}-errors`);
    };

    // Only setup entity observers for direct ServiceStore instances
    // Subclasses must call setupEntityObservers() after their makeAutoObservable
    if (this.constructor === ServiceStore) {
      this.setupEntityObservers();
    }
  }

  /**
   * Setup onBecomeObserved/onBecomeUnobserved for entities.
   * Must be called AFTER makeAutoObservable to ensure entities is observable.
   * Subclasses should call this after their own makeAutoObservable.
   */
  protected setupEntityObservers() {
    onBecomeObserved(this, "entities", () =>
      this.handleBecomeObserved("resume")
    );
    onBecomeUnobserved(this, "entities", () =>
      this.handleBecomeObserved("suspend")
    );
  }

  handleBecomeObserved = (/** @type {string} */ value: string) => {
    if (this.debugMode)
      console.log(
        `${this.name} - handleBecomeObserved entities - value ${value}`
      );
    if (value === "resume" && !this.enableAutoChannel) {
      runInAction(() => {
        this.enableAutoChannel = true;
      });
    }
    if (value === "suspend" && this.enableAutoChannel) {
      runInAction(() => {
        this.enableAutoChannel = false;
      });
    }
    return value;
  };

  errorHandler = (error: ErrorHandlerError): ErrorHandlerError => {
    const { name, message } = error;
    console.error("error on service: ", this.name);
    console.error("error", error);
    if (name === "NotAuthenticated") {
      if (this.rootStore.routerStore) {
        this.handleNotAuthenticated(message);
      }
    }
    if (name === "NotFound") {
      if (error?.hook?.id) {
        this.removeEvent({ id: error.hook.id.toString() });
      }
    }

    if (this.enableErrorNotifications) {
      this.handleErrorNotification(name, message);
    }
    return error;
  };

  setObservability = (/** @type {boolean} */ value: boolean) => {
    this.debugMode &&
      console.warn(`Debug setObservability ${this.name}, ${value}`);

    if (value && !this.firstLoad) this.handleFirstLoad();
    this.isBeingWatched = value;
    return value;
  };

  handleErrorNotification = (
    /** @type {string} */ name: string,
    /** @type {string} */ message: string
  ) => {
    console.warn("handleErrorNotification", name, message);
    if (this.rootStore.notificationStore) {
      this.rootStore.notificationStore.addNotification({
        message: message,
        options: { variant: "warning" },
      });
    }
    return message;
  };

  handleFirstLoad = () => {
    if (!this.firstLoad && this.storage) {
      runInAction(() => {
        this.firstLoad = true;
      });
    }
    return;
  };

  handleNotAuthenticated = async (/** @type {string} */ message: string) => {
    if (!this.rootStore.routerStore || !this.rootStore.authStore) {
      console.warn("User was not authenticated", message);
      return;
    }
    const { handleAuthReroute, params, queryParams } =
      this.rootStore.routerStore;
    const route = this.rootStore.routerStore.route;
    const checkRefresh = await this.rootStore.authStore.routerStoreAuthCheck();
    if (!checkRefresh) {
      if (this.rootStore.notificationStore) {
        this.rootStore.notificationStore.addNotification({
          message: message,
          options: { variant: "warning" },
        });
      } else {
        console.warn("User was not authenticated", message);
      }
      if (route) {
        return handleAuthReroute(route, params, queryParams);
      }
    }
  };

  setServiceIdField = async (/** @type {string} */ idField: string) => {
    this.debugMode && console.warn("Debug setServiceIdField for", idField);
    if (idField !== this.idField) {
      runInAction(() => {
        this.idField = idField;
      });
    }
    // if (
    //   name === 'api-services' ||
    //   (this.rootStore.mode.includes('dev') && name.includes('azure/')) ||
    //   (!this.rootStore.mode.includes('prod') && name.includes('sage/'))
    // ) {
    //   return name;
    // }
    // if (this.rootStore.mode === 'test') {
    //   runInAction(() => {
    //     this.idField = 'id';
    //   });
    //   return name;
    // }
    // try {
    //   const { id, jobNumberService = false } = await this.rootStore.app
    //     .service('api-services')
    //     .get(name);
    //   this.debugMode && console.warn('Debug setServiceIdField found idField', id);
    //   runInAction(() => {
    //     if (id && id !== this.idField) {
    //       this.idField = id;
    //     }
    //     if (jobNumberService) {
    //       this.jobNumberService = jobNumberService;
    //     }
    //   });
    // } catch (err) {
    //   console.warn(`${name}: setServiceIdField Error`, err);
    // }
    return idField;
  };

  // handleStorageSync = async (
  //   /** @type {{ getItem: (arg0: string) => any; setItem: (arg0: string, arg1: string) => any; }} */ storage
  // ) => {
  //   this.debugMode && console.warn('Debug handleStorageSync', this.name);
  //   try {
  //     const loadedStoreItems = await storage.getItem(
  //       `${this.name}-store-items`
  //     );
  //     if (loadedStoreItems) this.loadStoreItems(JSON.parse(loadedStoreItems));
  //     this.syncStorageDispose = reaction(
  //       () => this.watchItems,
  //       async items => {
  //         this.debugMode &&
  //           console.warn('Debug handleStorageSync item count', items.length);
  //         await storage.setItem(
  //           `${this.name}-store-items`,
  //           JSON.stringify({ items: [...items] })
  //         );
  //       },
  //       { delay: 7500 }
  //     );
  //   } catch (err) {
  //     console.warn('handleStorageSync Error', err);
  //   }
  // };

  loadStorage = () => {
    if (!this.storage) {
      return;
    }
    const loadedItems: ServiceStoreItem[] = [];
    const storageHandler = (item: ServiceStoreItem) => {
      loadedItems.push(item);
    };
    this.storage
      .iterate((value: ServiceStoreItem) => {
        return storageHandler(value);
      })
      .then(() => {
        this.mergeItems(loadedItems);
        if (this.debugMode)
          console.warn("Storage Loaded For Store:", this.name);
      })
      .catch(function (err: unknown) {
        // This code runs if there were any errors
        console.warn(err);
      });
  };

  handleEvents = () => {

    if (!this.service || !this.service.on) {
      console.warn(
        `Service ${this.name} does not have .on method. Skipping event listeners setup.`
      );
      return;
    }

    this.service.on('created', async (item: ServiceStoreItem) => {
      this.addOrUpdateEvent(item, true);
    });
    this.service
      .on('patched', async (item: ServiceStoreItem) => {
        this.addOrUpdateEvent(item, true);
      });
    this.service.on('removed', async (item: ServiceStoreItem) => {
      this.removeEvent(item);
    });
    // // // // !this.disableUpdatedListener &&
    // const handleUpdated = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on updated', item);
    //     this.addOrUpdateEvent(item, true);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('updated', (item: any) => handleUpdated);
    // // // // !this.disablePatchedListener &&
    // // if (this.name === 'user') {
    // //   console.log('SETTING UP USER PATCH LISTENER');
    // //   console.log('this.service', this.service);
    // // }
    // const handlePatched = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on patched', item);
    //     this.addOrUpdateEvent(item, true);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('patched', async (item: any) => handlePatched);
    // // // // !this.disableRemovedListener &&
    // const handleRemoved = (item: any) => {
    //   runInAction(() => {
    //     console.warn('Debug on removed', item);
    //     this.removeEvent(item);
    //   });
    // };
    // this.rootStore.app
    //   .service(this.name)
    //   .on('removed', async (item: any) => handleRemoved);
    // !this.disableStatusListener &&
    //   this.service.on(
    //     'status',
    //     (
    //       /** @type {{ message: string; ids: string | any[]; }} */ status: {
    //         message: string;
    //         ids: string | any[];
    //       }
    //     ) => {
    //       console.warn('service on status', status);
    //       if (status && status.message) {
    //         if (status.message === 'remove') {
    //           runInAction(() => {
    //             const newArray = this.items
    //               .slice()
    //               .filter(
    //                 (arrayItem: { [key: string]: any }) =>
    //                   !status.ids.includes(arrayItem[this.idField])
    //               );
    //             this.replaceAllItems(newArray);
    //           });
    //           if (this.rootStore.notificationStore)
    //             this.rootStore.notificationStore.addNotification({
    //               message: `Removed ${status.ids.length} old record(s)`,
    //               options: { variant: 'info' },
    //             });
    //         } else {
    //           console.warn('service on status', status.message);
    //           if (this.rootStore.notificationStore)
    //             this.rootStore.notificationStore.addNotification({
    //               message: status.message,
    //               options: { variant: 'info' },
    //             });
    //         }
    //       }
    //     }
    //   );
  };

  dispose = () => {
    this.debugMode && console.log("DISPOSE CALLED FOR SERVICE:", this.name);

    // Clear all reaction disposers to prevent memory leaks
    try {
      this.firsLoadDispose && this.firsLoadDispose();
      this.disposeEntityOptimization && this.disposeEntityOptimization();

      // OPTIMIZED: Dispose batched error reactions
      this.errorReactionsBatchDispose && this.errorReactionsBatchDispose();
      if (this.reactionManager) {
        this.reactionManager.disposeAll();
        this.reactionManager = null;
      }
    } catch (error) {
      console.error(`Error disposing reactions for ${this.name}:`, error);
    }

    // Remove all event listeners properly to prevent memory leaks
    if (this.channelsEnabled && this.service) {
      try {
        this.service.removeAllListeners("created");
        this.service.removeAllListeners("updated");
        this.service.removeAllListeners("patched");
        this.service.removeAllListeners("removed");
        this.service.removeAllListeners("status");
      } catch (error) {
        console.error(`Error removing event listeners for ${this.name}:`, error);
      }
    }

    // Clear storage reference
    this.storage = null;

    // Clear all observable state to help with garbage collection
    runInAction(() => {
      this.items = [];
      this.pageDataSet = [];
      this.clearErrors();
      this.clearPending();
    });

    this.debugMode && console.log(`ServiceStore ${this.name} disposed successfully`);
  };

  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  // loadStoreItems = (/** @type {{ items: any; }} */ loadedStoreItems) => {
  //   this.debugMode && console.warn('Debug loadStoreItems', loadedStoreItems);
  //   this.items = [...loadedStoreItems.items];
  //   return this.items;
  // };

  enablePagination = (/** @type { number} */ pageSize: number) => {
    this.debugMode && console.warn("Debug enablePagination", pageSize);
    this.pagination.enabled = true;
    if (!isNaN(pageSize)) this.pagination.pageSize = pageSize;
  };

  setPageTotal = (/** @type {number} */ newTotal: number) => {
    this.debugMode && console.warn("Debug setPageTotal", newTotal);
    this.pagination.total = newTotal;
  };

  setPageQuery = (/** @type {object} */ query: object) => {
    this.debugMode && console.warn("Debug setPageQuery", query);
    this.pagination.query = query;
  };
  setPageSortKey = (/** @type {string} */ sortKey: string) => {
    this.debugMode && console.warn("Debug setPageSortKey", sortKey);
    this.pagination.sortKey = sortKey;
  };
  setPageSortDirection = (/** @type {string} */ sortDirection: string) => {
    if (!SORT_DIRECTION_OPTIONS.includes(sortDirection)) {
      console.error(
        "Requires one of [",
        SORT_DIRECTION_OPTIONS.join(", "),
        `] but was given: "${sortDirection}"`
      );
      return;
    }
    this.debugMode && console.warn("Debug setPageSortDirection", sortDirection);
    this.pagination.sortDirection = sortDirection;
  };

  updatePage = async (/** @type {number} */ newPage: number) => {
    this.debugMode && console.warn("Debug updatePage", newPage);
    if (!this.pagination.enabled)
      throw Error("Enable pagination to use this action");
    this.pagination.currentPage = newPage;
    return await this.find();
  };

  /**
   * @param {object[]} newItemArray
   * @description
   */
  mergeItems = (newItemArray: ServiceStoreItem[]) => {
    // console.log('newItemArray', newItemArray);
    this.debugMode &&
      console.warn("Debug mergeItems newItemArray", newItemArray);
    const newItemIds = newItemArray.map((item: ServiceStoreItem) => item.id);
    // console.log('newItemIds', newItemIds);

    const entitiesNotIncludedInNewItemArray = this.entities
      .slice()
      .filter((item: ServiceStoreItem) => !newItemIds.includes(item.id as string | number));

    // console.log('entitiesNotIncludedInNewItemArray', entitiesNotIncludedInNewItemArray);

    const combinedItems = [
      ...entitiesNotIncludedInNewItemArray,
      ...newItemArray,
    ];
    // console.log('combinedItems', combinedItems);
    this.debugMode && console.log("combinedItems.length", combinedItems.length);
    this.replaceAllItems(combinedItems);
  };

  /**
   * @param {object[]} newItemArray
   * @description
   */
  replaceAllItems = (newItemArray: object[]) => {
    this.debugMode &&
      console.warn("Debug replaceAllItems newItemArray", newItemArray);
    if (!newItemArray) {
      throw new Error("Requires items array");
    }
    this.debugMode &&
      console.warn(
        "Debug replaceAllItems this.items.length before",
        this.items.length
      );
    // console.log('replaceAllItems newItemArray', newItemArray);
    // @ts-expect-error - mobx types are not correct
    this.items.replace(newItemArray);
    // runInAction(() => {
    //   // console.log('this.items before', this.items);
    //   this.items = [...newItemArray];
    //   // console.log('this.items after', this.items);
    // });

    this.debugMode &&
      console.warn(
        "Debug replaceAllItems this.items.length after",
        this.items.length
      );
  };

  /**
   * @function addOrUpdateEvent
   * @param {object} item
   * @param {boolean} shouldSetStorage
   * @returns {object}
   * @description Checks to see if the item is already in the items array and updates it or adds if it is missing.
   */
  addOrUpdateEvent = (
    item: ServiceStoreItem,
    shouldSetStorage: boolean = false
  ): object => {
    this.debugMode && console.warn("Debug addOrUpdateEvent item", item);

    const foundIndex = this.items.findIndex(
      (arrayItem: ServiceStoreItem) =>
        arrayItem[this.idField] === item[this.idField]
    );

    if (foundIndex === -1) {
      this.items.push(item);
      if (shouldSetStorage && this.storage) {
        this.debugMode &&
          console.warn(
            "Debug addOrUpdateEvent storage.setItem",
            `${item.id}`,
            item
          );
        const safeItem = this.safeSerializeForStorage(item);
        this.storage
          .setItem(`${item.id}`, safeItem as ServiceStoreItem)
          .catch((error: unknown) => {
            this.handleStorageError(error, item);
          });
      }
    } else {
      this.items[foundIndex] = Object.assign(this.items[foundIndex], item);

      if (shouldSetStorage && this.storage) {
        this.debugMode &&
          console.warn(
            "Debug addOrUpdateEvent storage.setItem",
            `${item.id}`,
            this.items[foundIndex]
          );
        const safeItem = this.safeSerializeForStorage(this.items[foundIndex]);
        this.storage
          .setItem(`${this.items[foundIndex].id}`, safeItem as ServiceStoreItem)
          .catch((error: unknown) => {
            this.handleStorageError(error, this.items[foundIndex]);
          });
      }
    }

    return item;
  };

  /**
   * @function removeEvent
   * @param {object | number} item
   * @description
   */
  removeEvent = async (item: ServiceStoreItem | { id: string } | number) => {
    this.debugMode && console.warn("Debug removeEvent", item);
    if (!item) {
      console.log("Needs something to remove");
      return;
    }
    if (this.items.length === 0) {
      console.log("Current device did not have any items to remove");
      return;
    }

    this.debugMode && console.warn("Debug removeEvent for items", item);
    const id: string | number = typeof item === "number" ? item : (item as { id: string | number }).id;
    if (this.storage) {
      const foundItem = (await this.storage
        .getItem(`${id}`)
        .catch((error: unknown) => {
          console.error(
            `Something went wrong trying to get an item from ${this.name}Store`,
            error
          );
        })) as ServiceStoreItem | null;
      if (foundItem)
        this.storage.removeItem(`${id}`).catch((error: unknown) => {
          console.error(
            `Something went wrong trying to remove an item from ${this.name}Store`,
            error
          );
        });
    }
    const newArray = this.items
      .slice()
      .filter((arrayItem: ServiceStoreItem) => id !== arrayItem[this.idField]);
    this.replaceAllItems(newArray);
  };

  clearAll = () => {
    this.debugMode && console.warn("Debug clearAll", this.name);
    this.clearData();
    this.clearErrors();
    this.clearPending();
    this.clearStorage();
  };

  clearData = () => {
    this.debugMode && console.warn("Debug clearData", this.name);
    this.items = [];
    if (this.pagination.enabled) {
      this.clearPageData();
    }
  };

  clearPageData = () => {
    this.debugMode && console.warn("Debug clearData", this.name);
    if (this.pagination.enabled) {
      this.pagination = {
        enabled: true,
        total: 0,
        currentPage: 0,
        pageSize: 250,
        query: {},
        sortKey: "",
        sortDirection: SORT_DIRECTION_OPTIONS[0],
      };
    }
  };

  clearStorage = async () => {
    this.debugMode &&
      console.warn("Debug clearStorage", `${this.name}-store-items`);
    if (this.storage) {
      await this.storage.clear().catch((error: unknown) => {
        console.error(
          `Something went wrong trying to remove all items from ${this.name}Store`,
          error
        );
      });
    }
  };

  clearErrors = () => {
    this.debugMode && console.warn("Debug clearErrors", this.name);
    this.errorOnGet = null;
    this.errorOnFind = null;
    this.errorOnCreate = null;
    this.errorOnPatch = null;
    this.errorOnRemove = null;
  };

  clearPending = () => {
    this.debugMode && console.warn("Debug clearPending", this.name);
    this.isGetPending = false;
    this.isFindPending = false;
    this.isCreatePending = false;
    this.isPatchPending = false;
    this.isRemovePending = false;
  };
  /**
   *
   * @param {number} id
   * @param {object} params
   * @returns
   */
  get = async (id: number, params: object = {}) => {
    this.debugMode && console.warn("Debug get", id, params);
    this.isGetPending = true;
    let response: GetResponse | null = null;
    try {
      response = await this.service.get(id, params);
      runInAction(async () => {
        if (response) {
          this.addOrUpdateEvent(response, true);
        }
        this.isGetPending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnGet = error as ServiceError;
        this.isGetPending = false;
      });
    }
    if (response) return response;
  };

  // /**
  //  * findOne
  //  * @param {object} params
  //  * @returns
  //  */
  // findOne = async (params: object = {}) => {
  //   this.isFindPending = true;
  //   let response: any;

  //   try {
  //     this.debugMode && console.warn('Debug findOne', params);
  //     response = await this.service.findOne(params);
  //     runInAction(async () => {
  //       if (response) {
  //         this.debugMode && console.warn('Debug findOne response', response);
  //         this.addOrUpdateEvent(response, true);
  //       } else {
  //         this.debugMode &&
  //           console.warn('Debug findOne response no result', response);
  //       }
  //       this.isFindPending = false;
  //     });
  //   } catch (error: unknown) {
  //     runInAction(() => {
  //       this.errorOnFind = error;
  //       this.isFindPending = false;
  //     });
  //   }
  //   if (response) return response;
  // };

  /**
   *
   * @param {object} params
   * @returns
   */
  find = async (params: { query?: { [key: string]: unknown } } = {}) => {
    this.isFindPending = true;
    let response: FindResponse | null = null;

    try {
      if (this.pagination.enabled) {
        if (!params.query)
          Object.assign(params, {
            query: {},
          });

        if (
          params.query &&
          Object.keys(params.query).length &&
          Object.keys(this.pagination.query).length !==
          Object.keys(params.query).length
        ) {
          this.setPageQuery(params.query);
        }

        Object.assign(params.query || {}, {
          $limit: this.pagination.pageSize,
          $skip: this.pagination.pageSize * this.pagination.currentPage,
        });
        if (Object.keys(this.pagination.query).length) {
          Object.assign(params.query || {}, {
            ...this.pagination.query,
          });
        }
      }
      this.debugMode && console.warn("Debug find", params);
      response = await this.service.find(params);
      runInAction(async () => {
        if (response && response.data) {
          // console.log(`Service: ${this.name} response.data:`, response.data);
          this.debugMode && console.warn("Debug find response data", response);
          if (response.data.length) {
            response.data.map((item: ServiceStoreItem) =>
              this.addOrUpdateEvent(item, true)
            );
            return;
          }

          if (this.pagination.enabled) {
            if (response.total !== this.pagination.total)
              this.setPageTotal(response.total || response.data.length);
            await this.setPageDataSet({
              pageNumber: this.pagination.currentPage,
              pageItems: (response.data as ServiceStoreItem[]).map(
                (item: ServiceStoreItem) =>
                  item[this.idField] as ServiceStoreItem
              ),
            });
          }
        } else if (
          response &&
          response.result &&
          response.result.data &&
          response.result.data.length
        ) {
          this.debugMode &&
            console.warn("Debug find response result", response);
          if (response.result.data.length)
            response.result.data.map((item: ServiceStoreItem) =>
              this.addOrUpdateEvent(item, true)
            );

          if (this.pagination.enabled) {
            if (response.total !== this.pagination.total)
              this.setPageTotal(response.total || response.result.data.length);
            await this.setPageDataSet({
              pageNumber: this.pagination.currentPage,
              pageItems: (response.data as ServiceStoreItem[]).map(
                (item: ServiceStoreItem) =>
                  item[this.idField] as ServiceStoreItem
              ),
            });
          }
        } else {
          this.debugMode &&
            console.warn("Debug find response no data or result", response);
        }
        this.isFindPending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnFind = error as ServiceError;
        this.isFindPending = false;
      });
    }
    return response;
  };

  /**
   * create
   * @param {object} data
   * @param {object} params
   * @returns response
   */
  create = async (data: object, params: object = {}) => {
    this.debugMode && console.warn("Debug create", data, params);
    this.isCreatePending = true;
    let response: CreateResponse | null = null;
    try {
      response = await this.service.create(data, params);
      runInAction(async () => {
        if (!this.channelsEnabled) {
          if (response) {
            this.debugMode &&
              console.warn("Debug find response data", response);

            this.addOrUpdateEvent(response, true);
          } else {
            this.debugMode &&
              console.warn("Debug find response no data or result", response);
          }
        }
        this.isCreatePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnCreate = error as ServiceError;
        this.isCreatePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * patch
   * @param {number} id
   * @param {object} data
   * @param {object} params
   * @returns response
   */
  patch = async (id: number | null, data: object, params: object = {}) => {
    this.debugMode && console.warn("Debug patch", id, data, params);
    this.isPatchPending = true;
    let response: PatchResponse | null = null;
    try {
      response = await this.service.patch(id, data, params);
      runInAction(async () => {
        console.log('PATCH response', response);
        console.log('this.channelsEnabled', this.channelsEnabled);
        if (!this.channelsEnabled) {
          if (response && response.id) {
            this.debugMode &&
              console.warn("Debug find response data", response);
            this.addOrUpdateEvent(response, true);
          } else if (response && response.data) {
            this.debugMode &&
              console.warn("Debug find response data", response);
            if (Array.isArray(response.data) && response.data.length)
              response.data.map((item: ServiceStoreItem) =>
                this.addOrUpdateEvent(item, true)
              );
          }
          // else if (
          //   response.result &&
          //   response.result.data &&
          //   response.result.data.length
          // ) {
          //   this.debugMode &&
          //     console.warn('Debug find response result', response);

          //   response.result.data.map((item: ServiceStoreItem) =>
          //     this.addOrUpdateEvent(item, true)
          //   );
          // }
          else {
            this.debugMode &&
              console.warn("Debug find response no data or result", response);
          }
        }
        this.isPatchPending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnPatch = error as ServiceError;
        this.isPatchPending = false;
      });
    }
    if (response) return response;
  };

  /**
   * update
   * @param {number} id
   * @param {object} data
   * @param {object} params
   * @returns response
   */
  update = async (id: number, data: object, params: object = {}) => {
    this.debugMode && console.warn("Debug update", id, data);
    this.isUpdatePending = true;
    let response: UpdateResponse | null = null;
    try {
      response = await this.service.update(id, data, params);
      runInAction(async () => {
        if (!this.channelsEnabled) {
          if (response && response.id) {
            this.debugMode &&
              console.warn("Debug find response data", response);
            this.addOrUpdateEvent(response, true);
          } else if (response && response.data) {
            this.debugMode &&
              console.warn("Debug find response data", response);
            if (Array.isArray(response.data) && response.data.length)
              response.data.map((item: ServiceStoreItem) =>
                this.addOrUpdateEvent(item, true)
              );
          }
          // else if (
          //   response &&
          //   response.result &&
          //   response.result.data &&
          //   response.result.data.length
          // ) {
          //   this.debugMode &&
          //     console.warn('Debug find response result', response);

          //   response.result.data.map((item: any) =>
          //     this.addOrUpdateEvent(item, true)
          //   );
          // }
          else {
            this.debugMode &&
              console.warn("Debug find response no data or result", response);
          }
        }
        this.isUpdatePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnRemove = error as ServiceError;
        this.isUpdatePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * remove
   * @param {number} id
   * @param {object} params
   * @returns response
   */
  remove = async (id: number | null, params: object = {}) => {
    this.debugMode && console.warn("Debug remove", id, params);
    let response: RemoveResponse | null = null;
    try {
      this.isRemovePending = true;
      response = await this.service.remove(id, params);
      runInAction(async () => {
        if (!this.channelsEnabled && response) {
          if (response.id) {
            this.debugMode &&
              console.warn("Debug find response data", response);
            this.removeEvent({ id: response.id.toString() });
          } else if (Array.isArray(response)) {
            response.map(async (i: ServiceStoreItem) => this.removeEvent(i));
          }
        }
        this.isRemovePending = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.errorOnRemove = error as ServiceError;
        this.isRemovePending = false;
      });
    }
    if (response) return response;
  };

  /**
   * setPageDataSet
   * @param {object} item
   */
  setPageDataSet = async (item: {
    pageNumber: number;
    pageItems: ServiceStoreItem[];
  }) => {
    const foundPageIndex = this.pageDataSet.findIndex(
      (i: { pageNumber: number }) => i.pageNumber === item.pageNumber
    );
    if (foundPageIndex > -1) {
      this.pageDataSet[foundPageIndex] = {
        pageNumber: item.pageNumber,
        pageItems: item.pageItems,
      };
    } else {
      this.pageDataSet.push({
        pageNumber: item.pageNumber,
        pageItems: item.pageItems,
      });
    }
  };

  getById(id: number | string): ServiceStoreItem | undefined {
    return this.entities.find(entity => (entity as ServiceStoreItem)[this.idField] as number === id as number);
  }

  filterByKeyValue(key: string, value: unknown): ServiceStoreItem[] {
    return this.entities.filter(entity => (entity as ServiceStoreItem)[key] === value);
  }

  // get watchItems() {
  //   return this.items.slice().map(item => {
  //     return { ...item };
  //   });
  // }

  get entities() {
    // console.log('entities this.items', this.items);
    if (this.enableEntityOptimization && !this.isBeingWatched) return [];
    return this.items.slice().map((item: ServiceStoreItem) => {
      return { ...item };
    });
  }

  get currentPageItems() {
    if (!this.pagination.enabled) return [];
    return this.entities
      .slice()
      .filter((item: ServiceStoreItem) => {
        const foundPageIndex = this.pageDataSet.findIndex(
          (i: { pageNumber: number; pageItems: ServiceStoreItem[] }) => i.pageNumber === this.pagination.currentPage
        );
        if (foundPageIndex > -1) {
          const pageItems = this.pageDataSet[foundPageIndex].pageItems;
          return pageItems.some((pageItem) => pageItem[this.idField] === item[this.idField]);
        } else return false;
      })
      .sort((a: ServiceStoreItem, b: ServiceStoreItem) => {
        if (!this.pagination.sortKey) return 0;
        if (this.pagination.sortDirection === "ASC")
          return (
            (a[this.pagination.sortKey] as number) -
            (b[this.pagination.sortKey] as number)
          );
        return (
          (b[this.pagination.sortKey] as number) -
          (a[this.pagination.sortKey] as number)
        );
      })
      .map((item: ServiceStoreItem) => {
        return { ...item };
      });
  }
  get totalPages() {
    if (!this.pagination.enabled) return "N/A";
    return Math.ceil(this.pagination.total / this.pagination.pageSize - 0.001);
  }

  get isServicePending() {
    return (
      this.isGetPending ||
      this.isFindPending ||
      this.isCreatePending ||
      this.isPatchPending ||
      this.isRemovePending
    );
  }

  get serviceHasError() {
    return (
      !!this.errorOnGet ||
      !!this.errorOnFind ||
      !!this.errorOnCreate ||
      !!this.errorOnPatch ||
      !!this.errorOnRemove
    );
  }

  get shouldRunFirstLoad() {
    return this.firstLoad;
  }

  /**
   * @function safeSerializeForStorage
   * @param {object} item 
   * @description Safely serialize an object for IndexedDB storage by removing non-serializable properties
   */
  private safeSerializeForStorage = (item: { [key: string]: unknown }): object => {
    try {
      // Use JSON stringify/parse to remove non-serializable properties
      return JSON.parse(JSON.stringify(item));
    } catch (error) {
      // Fallback: create a clean object with only primitive values
      const safeItem: { [key: string]: unknown } = {};
      for (const [key, value] of Object.entries(item)) {
        if (value !== null && value !== undefined) {
          const valueType = typeof value;
          if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
            safeItem[key] = value;
          } else if (valueType === 'object' && !Array.isArray(value)) {
            // Try to serialize nested objects safely
            try {
              safeItem[key] = JSON.parse(JSON.stringify(value));
            } catch {
              // Skip non-serializable nested objects
              continue;
            }
          } else if (Array.isArray(value)) {
            // Handle arrays by filtering serializable items
            try {
              safeItem[key] = JSON.parse(JSON.stringify(value));
            } catch {
              // Skip non-serializable arrays
              continue;
            }
          }
        }
      }
      return safeItem;
    }
  };

  /**
   * @function handleStorageError
   * @param {unknown} error
   * @param {object} item
   * @description Handle storage errors, particularly IndexedDB version conflicts
   */
  private handleStorageError = async (error: unknown, item: { [key: string]: unknown }) => {
    if (error instanceof Error) {
      if (error.name === 'VersionError' || error.message.includes('version')) {
        console.warn(`IndexedDB version conflict for ${this.name}Store, clearing cache and retrying...`);
        try {
          // Clear the problematic store and retry
          await this.storage?.clear();
          const safeItem = this.safeSerializeForStorage(item);
          const itemWithMetadata = {
            ...safeItem,
            _storeVersion: Date.now(),
            _lastUpdated: new Date().toISOString()
          };
          await this.storage?.setItem(`${item.id}`, itemWithMetadata as unknown as ServiceStoreItem);
        } catch (retryError) {
          console.warn(`Failed to resolve IndexedDB version conflict for ${this.name}Store:`, retryError);
        }
      } else {
        console.error(
          `Something went wrong trying to add an item from ${this.name}Store`,
          error
        );
      }
    }
  };
}
